import { useLazyQuery, useMutation } from "@apollo/client";
import { Box, GridLegacy as Grid, useTheme } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { gql } from "generated-graphql";
import { JobStepsDone } from "generated-graphql/graphql";
import { compact, orderBy, sortBy } from "lodash-es";
import { useCallback, useMemo, useState } from "react";

import type { ReportJob } from "./types";

import { GET_FACILITY_DETAILS } from "../useFacilityDetails";
import { GET_TIERII_REPORT, useReportDetails } from "../useReportDetails";
import { DeleteStepConfirmDialog } from "./DeleteStepConfirmDialog";
import { JobDetailTabs } from "./JobDetailTabs";
import { JobListPane } from "./JobListPane";

const DELETE_JOB_STEPS_DONE = gql(`
  mutation DeleteJobStepsDone($id: ID!) {
    deleteJobStepsDone(id: $id) {
      id
      name
    }
  }
`);

const GET_CHEMICALS_BY_IDS = gql(`
  query GetChemicalsForStepDeletion($ids: [ID!]!) {
    chemicalsByIds(ids: $ids) {
      id
      name
    }
  }
`);

export function WebAutomationJobInfo() {
  const theme = useTheme();
  const { data } = useReportDetails();
  const jobs = useMemo(
    () => orderBy(data?.tierIIReport.facility.jobs ?? [], ["updatedAt"], ["desc"]) as ReportJob[],
    [data?.tierIIReport?.facility?.jobs]
  );

  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const effectiveSelectedId =
    selectedJobId && jobs.some((j) => j.id === selectedJobId)
      ? selectedJobId
      : (jobs[0]?.id ?? null);

  const selectedJob = useMemo(
    () => jobs.find((j) => j.id === effectiveSelectedId) ?? null,
    [jobs, effectiveSelectedId]
  );

  const logs = sortBy(compact(selectedJob?.logs), (log) => log.createdAt);
  const stepsDone = compact(selectedJob?.stepsDone);
  const screenshotSrcs = compact(
    logs.map(
      (log) =>
        log.screenshotUrl && {
          src: log.screenshotUrl,
          createdAt: log.createdAt,
          message: log.message,
        }
    )
  ).reverse();

  const handleSelectJob = useCallback((job: ReportJob) => {
    setSelectedJobId(job.id);
  }, []);

  const alerts = useAlerts();
  const [stepToDelete, setStepToDelete] = useState<JobStepsDone | null>(null);

  const [deleteJobStep, { loading: deleteLoading }] = useMutation(DELETE_JOB_STEPS_DONE, {
    refetchQueries: [GET_TIERII_REPORT, GET_FACILITY_DETAILS],
    awaitRefetchQueries: true,
    onCompleted: () => {
      alerts.success("Job step deleted successfully");
      setStepToDelete(null);
    },
    onError: (error) => {
      alerts.error(`Failed to delete job step: ${error.message}`);
    },
  });

  const [fetchChemical, { data: chemicalData, loading: chemicalLoading }] = useLazyQuery(
    GET_CHEMICALS_BY_IDS,
    {
      fetchPolicy: "network-only",
    }
  );

  const extractChemicalId = useCallback((name: string | null | undefined): string | null => {
    if (!name) {
      return null;
    }
    const match = name.match(/^chemical-([0-9a-fA-F-]{36})$/);
    return match ? match[1] : null;
  }, []);

  const chemicalName = useMemo(() => {
    if (!stepToDelete) return null;
    const chemicalId = extractChemicalId(stepToDelete.name);
    if (!chemicalId || !chemicalData?.chemicalsByIds?.[0]) return null;
    return chemicalData.chemicalsByIds[0].name ?? null;
  }, [stepToDelete, chemicalData, extractChemicalId]);

  const handleDeleteConfirm = useCallback(() => {
    if (stepToDelete) {
      deleteJobStep({ variables: { id: stepToDelete.id } });
    }
  }, [stepToDelete, deleteJobStep]);

  const handleDeleteStep = useCallback(
    (step: JobStepsDone) => {
      setStepToDelete(step);
      const chemicalId = extractChemicalId(step.name);
      if (chemicalId) {
        fetchChemical({ variables: { ids: [chemicalId] } });
      }
    },
    [extractChemicalId, fetchChemical]
  );

  return (
    <Grid
      item
      container
      sx={{
        paddingTop: theme.spacing(2),
        backgroundColor: theme.palette.background.paper,
        borderRadius: 1,
      }}
    >
      <Grid container sx={{ minHeight: "500px" }}>
        <JobListPane jobs={jobs} selectedJob={selectedJob} onSelectJob={handleSelectJob} />

        <Grid item xs={12} md={8} sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
          <Box sx={{ p: theme.spacing(2), flex: 1, overflow: "auto" }}>
            <JobDetailTabs
              job={selectedJob}
              logs={logs}
              stepsDone={stepsDone}
              screenshotSrcs={screenshotSrcs}
              onDeleteStep={handleDeleteStep}
            />
          </Box>
        </Grid>
      </Grid>

      <DeleteStepConfirmDialog
        open={!!stepToDelete}
        stepToDelete={stepToDelete}
        onClose={() => setStepToDelete(null)}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
        chemicalName={chemicalName}
        chemicalLoading={chemicalLoading}
      />
    </Grid>
  );
}
